import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { TAGADAPAY_PLATFORM_NAME } from '@/lib/branding/platform-name'
import { useForm, FormProvider, useFieldArray, Controller, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import type { TFunction } from 'i18next'
import { X, Scale, Percent, Maximize2, Sliders, Sparkles, AlertTriangle, Archive, EyeOff, Info } from 'lucide-react'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Slider } from '@/components/ui/slider'
import { Separator } from '@/components/ui/separator'
import { Button } from '@/components/ui/button'
import { ProcessorSelector } from '@/components/form/processor-selector'
import { usePaymentFlowFeatures } from '@/lib/api/paymentFlows'
import { useProcessorsList, Processor } from '@/lib/api/processors'
import { shouldWarnStandalone3dsUnsupported } from '@/lib/standalone-3ds-warning'
import { PaymentFlowDiagram } from '@/app/stores/[id]/payment-flows/flows/[flowId]/components/payment-flow-diagram'
import { cn } from '@/lib/utils'
import { ProcessorRoutingPins } from './ProcessorRoutingPins'
import { RoutingPrecedenceHelp } from './RoutingPrecedenceHelp'
import {
    CARD_BRANDS,
    CARD_BRAND_LABELS,
    GROUP_CAPTION_CLASSES,
    GROUP_LABEL_CLASSES,
    createRoutingSchema,
    formatPayfacProviderLabel,
} from './processor-routing'

const createPaymentFlowFormSchema = (t: TFunction) => {
    const processorConfigSchema = z.object({
        processorId: z.string().min(1, t('paymentFlows:validation.processorRequired')),
        weight: z.number().min(0).max(100),
        disabled: z.boolean(),
        nonStickable: z.boolean(),
        disabledCardBrands: z.array(z.string()).optional(),
        routing: createRoutingSchema(t).optional(),
    })

    const fallbackProcessorConfigSchema = z.object({
        processorId: z.string().min(1, t('paymentFlows:validation.processorRequired')),
        orderIndex: z.number(),
    })

    return z.object({
        name: z.string().min(1, t('paymentFlows:validation.nameRequired')),
        pickProcessorStrategy: z.enum(['weighted', 'automatic']),
        fallbackMode: z.boolean(),
        maxFallbackRetries: z.number().min(0).max(10),
        threeDsEnabled: z.boolean(),
        stickyProcessorEnabled: z.boolean(),
        processorConfigs: z.array(processorConfigSchema),
        fallbackProcessorConfigs: z.array(fallbackProcessorConfigSchema),
    })
}

export type PaymentFlowFormData = z.infer<ReturnType<typeof createPaymentFlowFormSchema>>
export type RoutingFormValue = NonNullable<PaymentFlowFormData['processorConfigs'][number]['routing']>
type ProcessorConfig = PaymentFlowFormData['processorConfigs'][number]
type ProcessorWarningItem = {
    id: string
    name: string
    location: 'primary' | 'fallback'
    payfacProvider?: string | null
}

interface PaymentFlowFormProps {
    initialData?: PaymentFlowFormData
    /** Server-computed on the saved flow. Undefined while unknown (create page, query loading). */
    hasStandalone3dsCapableProcessor?: boolean
    isPending: boolean
    onSubmit: (data: PaymentFlowFormData) => Promise<void>
    onCancel: () => void
    Header: ({
        isPending,
        onCancel,
    }: {
        isPending: boolean
        onCancel: () => void
    }) => React.ReactNode
}

const getDefaultFormData = (): PaymentFlowFormData => ({
    name: '',
    pickProcessorStrategy: 'weighted',
    fallbackMode: false,
    maxFallbackRetries: 2,
    threeDsEnabled: false,
    stickyProcessorEnabled: false,
    processorConfigs: [],
    fallbackProcessorConfigs: [],
})

const calculateTotalWeight = (configs: ProcessorConfig[]): number =>
    configs
        .filter((c) => !c.disabled)
        .reduce((sum, c) => sum + c.weight, 0)

// Color palette for weight bars
const WEIGHT_COLORS = [
    'bg-blue-500',
    'bg-emerald-500',
    'bg-amber-500',
    'bg-purple-500',
    'bg-rose-500',
    'bg-cyan-500',
    'bg-orange-500',
    'bg-pink-500',
]

/**
 * Reusable form component for creating or editing payment flows
 */
export const PaymentFlowForm = ({
    initialData,
    hasStandalone3dsCapableProcessor,
    isPending,
    onSubmit,
    onCancel,
    Header,
}: PaymentFlowFormProps) => {
    const { t } = useTranslation(['paymentFlows', 'common'])
    const { data: paymentFlowFeatures } = usePaymentFlowFeatures()
    const isRoutingPinsEnabled = paymentFlowFeatures?.routingPinsEnabled === true

    const paymentFlowFormSchema = React.useMemo(() => createPaymentFlowFormSchema(t), [t])

    const methods = useForm<PaymentFlowFormData>({
        resolver: zodResolver(paymentFlowFormSchema),
        defaultValues: initialData || getDefaultFormData(),
    })

    const {
        handleSubmit,
        control,
        setValue,
        formState: { errors },
    } = methods

    const {
        fields: processorConfigFields,
        append: appendProcessorConfig,
        remove: removeProcessorConfig,
    } = useFieldArray({
        control,
        name: 'processorConfigs',
    })

    const {
        fields: fallbackConfigFields,
        append: appendFallbackConfig,
        remove: removeFallbackConfig,
    } = useFieldArray({
        control,
        name: 'fallbackProcessorConfigs',
    })

    // Watch for reactive updates to the weight distribution bar and preview
    const watchedProcessorConfigs = useWatch({ control, name: 'processorConfigs' })
    const watchedFallbackConfigs = useWatch({ control, name: 'fallbackProcessorConfigs' })
    const watchedStrategy = useWatch({ control, name: 'pickProcessorStrategy' })
    const watchedFallbackMode = useWatch({ control, name: 'fallbackMode' })
    const watchedMaxRetries = useWatch({ control, name: 'maxFallbackRetries' })
    const watchedThreeDsEnabled = useWatch({ control, name: 'threeDsEnabled' })

    // The toggle is live, the capability is what the server computed on the saved flow.
    const isStandalone3dsUnsupported = shouldWarnStandalone3dsUnsupported({
        threeDsEnabled: watchedThreeDsEnabled,
        hasStandalone3dsCapableProcessor,
    })

    // Fetch processors for the preview
    const { data: processorsData } = useProcessorsList()
    const processors = (processorsData || []) as Processor[]

    // Helper to get processor by ID
    const getProcessor = (processorId: string) => processors.find((p: Processor) => p.id === processorId)

    // Build paymentFlow object for the diagram
    const previewPaymentFlow = React.useMemo(() => ({
        id: 'preview',
        name: 'Preview',
        pickProcessorStrategy: watchedStrategy,
        fallbackMode: watchedFallbackMode,
        maxFallbackRetries: watchedMaxRetries,
        processorConfigs: (watchedProcessorConfigs || []).map(config => ({
            ...config,
            processor: getProcessor(config.processorId),
        })),
        fallbackProcessorConfigs: (watchedFallbackConfigs || []).map((config, index) => ({
            ...config,
            orderIndex: index,
            processor: getProcessor(config.processorId),
        })),
    }), [watchedProcessorConfigs, watchedFallbackConfigs, watchedStrategy, watchedFallbackMode, watchedMaxRetries, processors])

    // Check for archived processors in the configuration
    const archivedProcessorsInForm = React.useMemo(() => {
        const archived: ProcessorWarningItem[] = []
        
        // Check primary processors
        ;(watchedProcessorConfigs || []).forEach(config => {
            const processor = getProcessor(config.processorId)
            if (processor?.archivedAt) {
                archived.push({
                    id: processor.id,
                    name: processor.name,
                    location: 'primary',
                    payfacProvider: (processor as { payfacProvider?: string | null }).payfacProvider,
                })
            }
        })
        
        // Check fallback processors
        ;(watchedFallbackConfigs || []).forEach(config => {
            const processor = getProcessor(config.processorId)
            if (processor?.archivedAt) {
                archived.push({
                    id: processor.id,
                    name: processor.name,
                    location: 'fallback',
                    payfacProvider: (processor as { payfacProvider?: string | null }).payfacProvider,
                })
            }
        })
        
        return archived
    }, [watchedProcessorConfigs, watchedFallbackConfigs, processors])

    // Check for disabled processors in the configuration
    const disabledProcessorsInForm = React.useMemo(() => {
        const disabled: ProcessorWarningItem[] = []
        
        // Check primary processors
        ;(watchedProcessorConfigs || []).forEach(config => {
            const processor = getProcessor(config.processorId)
            if (processor && !processor.archivedAt && processor.enabled === false) {
                disabled.push({
                    id: processor.id,
                    name: processor.name,
                    location: 'primary',
                    payfacProvider: (processor as { payfacProvider?: string | null }).payfacProvider,
                })
            }
        })
        
        // Check fallback processors
        ;(watchedFallbackConfigs || []).forEach(config => {
            const processor = getProcessor(config.processorId)
            if (processor && !processor.archivedAt && processor.enabled === false) {
                disabled.push({
                    id: processor.id,
                    name: processor.name,
                    location: 'fallback',
                    payfacProvider: (processor as { payfacProvider?: string | null }).payfacProvider,
                })
            }
        })
        
        return disabled
    }, [watchedProcessorConfigs, watchedFallbackConfigs, processors])

    const handleAddProcessor = () => {
        appendProcessorConfig({
            processorId: '',
            weight: 0,
            disabled: false,
            nonStickable: false,
            disabledCardBrands: [],
            routing: { pins: [] },
        })
    }

    const handleAddFallbackProcessor = () => {
        appendFallbackConfig({
            processorId: '',
            orderIndex: fallbackConfigFields.length,
        })
    }

    const handleFormSubmit = async (data: PaymentFlowFormData) => {
        await onSubmit(data)
    }

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(handleFormSubmit)}>
                <div className="flex flex-col space-y-4 sm:space-y-6">
                    <Header isPending={isPending} onCancel={onCancel} />

                    {/* Warning banner for archived processors */}
                    {archivedProcessorsInForm.length > 0 && (
                        <Alert variant="destructive" className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/50">
                            <AlertTriangle className="size-4 text-amber-600 dark:text-amber-400" />
                            <AlertTitle className="text-amber-800 dark:text-amber-200">
                                {t('paymentFlows:warnings.archived_processors_in_form_title')}
                            </AlertTitle>
                            <AlertDescription className="text-amber-700 dark:text-amber-300">
                                <p>
                                    {t('paymentFlows:warnings.archived_processors_in_form_description')}
                                </p>
                                <ul className="mt-2 list-inside list-disc">
                                    {archivedProcessorsInForm.map(p => {
                                        const payfacProvider = (p as { payfacProvider?: string | null }).payfacProvider
                                        return (
                                        <li key={p.id} className="flex items-center gap-2">
                                            <Archive className="inline size-3" />
                                            <span className="font-medium">{p.name}</span>
                                            {payfacProvider && (
                                                <span className="rounded-md border bg-muted px-1.5 py-0 text-[10px] font-medium leading-4 text-muted-foreground">
                                                    {formatPayfacProviderLabel(payfacProvider)}
                                                </span>
                                            )}
                                            <span className="text-xs">({p.location === 'primary' ? t('paymentFlows:form.fields.processor_location.primary') : t('paymentFlows:form.fields.processor_location.fallback')})</span>
                                        </li>
                                        )
                                    })}
                                </ul>
                                <p className="mt-2">
                                    {t('paymentFlows:warnings.archived_processors_action')}
                                </p>
                            </AlertDescription>
                        </Alert>
                    )}

                    {/* Warning banner for disabled processors */}
                    {disabledProcessorsInForm.length > 0 && (
                        <Alert variant="destructive" className="border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950/50">
                            <AlertTriangle className="size-4 text-orange-600 dark:text-orange-400" />
                            <AlertTitle className="text-orange-800 dark:text-orange-200">
                                {t('paymentFlows:warnings.disabled_processors_in_form_title')}
                            </AlertTitle>
                            <AlertDescription className="text-orange-700 dark:text-orange-300">
                                <p>
                                    {t('paymentFlows:warnings.disabled_processors_in_form_description')}
                                </p>
                                <ul className="mt-2 list-inside list-disc">
                                    {disabledProcessorsInForm.map(p => {
                                        const payfacProvider = (p as { payfacProvider?: string | null }).payfacProvider
                                        return (
                                        <li key={p.id} className="flex items-center gap-2">
                                            <EyeOff className="inline size-3" />
                                            <span className="font-medium">{p.name}</span>
                                            {payfacProvider && (
                                                <span className="rounded-md border bg-muted px-1.5 py-0 text-[10px] font-medium leading-4 text-muted-foreground">
                                                    {formatPayfacProviderLabel(payfacProvider)}
                                                </span>
                                            )}
                                            <span className="text-xs">({p.location === 'primary' ? t('paymentFlows:form.fields.processor_location.primary') : t('paymentFlows:form.fields.processor_location.fallback')})</span>
                                        </li>
                                        )
                                    })}
                                </ul>
                                <p className="mt-2">
                                    {t('paymentFlows:warnings.disabled_processors_action')}
                                </p>
                            </AlertDescription>
                        </Alert>
                    )}

                    {/* Warning banner: standalone 3DS is on but no routed processor can perform it */}
                    {isStandalone3dsUnsupported && (
                        <Alert variant="destructive" className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/50">
                            <AlertTriangle className="size-4 text-red-600 dark:text-red-400" />
                            <AlertTitle className="text-red-800 dark:text-red-200">
                                {t('paymentFlows:warnings.standalone_3ds_unsupported_title')}
                            </AlertTitle>
                            <AlertDescription className="text-red-700 dark:text-red-300">
                                <p>{t('paymentFlows:warnings.standalone_3ds_unsupported_description')}</p>
                                <p className="mt-2">{t('paymentFlows:warnings.standalone_3ds_unsupported_action')}</p>
                            </AlertDescription>
                        </Alert>
                    )}

                    {/* Top Row: Basic Settings + Advanced Settings */}
                    <div className="grid gap-6 lg:grid-cols-2">
                        {/* Basic Settings */}
                        <Card>
                            <CardHeader>
                                <CardTitle>
                                    {t('paymentFlows:form.sections.basic_settings')}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <Controller
                                    name="name"
                                    control={control}
                                    render={({ field }) => (
                                        <div className="space-y-2">
                                            <Label htmlFor="name">
                                                {t('paymentFlows:form.fields.name.label')}
                                                {' *'}
                                            </Label>
                                            <Input
                                                id="name"
                                                value={field.value}
                                                onChange={field.onChange}
                                                onBlur={field.onBlur}
                                                placeholder={t('paymentFlows:form.fields.name.placeholder')}
                                                aria-invalid={!!errors.name}
                                            />
                                            {errors.name && (
                                                <p className="text-xs text-red-600">{errors.name.message}</p>
                                            )}
                                        </div>
                                    )}
                                />

                                <Controller
                                    name="threeDsEnabled"
                                    control={control}
                                    render={({ field }) => (
                                        <div className="space-y-2">
                                            <div className="flex items-center justify-between">
                                                <div className="space-y-0.5">
                                                    <Label htmlFor="threeDsEnabled">
                                                        {t('paymentFlows:form.fields.three_ds.label')}
                                                    </Label>
                                                    <p className="text-muted-foreground text-xs">
                                                        {t('paymentFlows:form.fields.three_ds.help', {
                                                            platformName: TAGADAPAY_PLATFORM_NAME,
                                                        })}
                                                    </p>
                                                </div>
                                                <Switch
                                                    id="threeDsEnabled"
                                                    checked={field.value}
                                                    onCheckedChange={field.onChange}
                                                />
                                            </div>
                                            {field.value ? (
                                                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs dark:border-amber-800 dark:bg-amber-950/30">
                                                    <p className="text-amber-800 dark:text-amber-200">
                                                        <strong>{t('paymentFlows:form.fields.three_ds.note_label')}</strong>{' '}
                                                        {t('paymentFlows:form.fields.three_ds.enabled_note', {
                                                            platformName: TAGADAPAY_PLATFORM_NAME,
                                                        })}
                                                    </p>
                                                </div>
                                            ) : (
                                                <p className="text-muted-foreground text-[11px]">
                                                    {t('paymentFlows:form.fields.three_ds.disabled_hint')}
                                                </p>
                                            )}
                                        </div>
                                    )}
                                />

                                <Controller
                                    name="stickyProcessorEnabled"
                                    control={control}
                                    render={({ field }) => (
                                        <div className="flex items-center justify-between">
                                            <div className="space-y-0.5">
                                                <Label htmlFor="stickyProcessorEnabled">
                                                    {t('paymentFlows:form.fields.sticky_processor.label')}
                                                </Label>
                                                <p className="text-muted-foreground text-xs">
                                                    {t('paymentFlows:form.fields.sticky_processor.help')}
                                                </p>
                                            </div>
                                            <Switch
                                                id="stickyProcessorEnabled"
                                                checked={field.value}
                                                onCheckedChange={field.onChange}
                                            />
                                        </div>
                                    )}
                                />
                            </CardContent>
                        </Card>

                        {/* Flow Preview */}
                        <Card className="overflow-hidden">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-base">
                                    {t('paymentFlows:form.sections.flow_preview')}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-0">
                                <div className="h-[280px]">
                                    <PaymentFlowDiagram paymentFlow={previewPaymentFlow} compact />
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Bottom Row: Primary Processors + Fallback Processors */}
                    <div className="grid gap-6 lg:grid-cols-2 items-start">
                    {/* Primary Processors */}
                    <Card>
                        <CardHeader>
                            <CardTitle>
                                {t('paymentFlows:form.sections.primary_processors')}
                            </CardTitle>
                            <CardDescription>
                                {t('paymentFlows:form.sections.primary_processors_description')}
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {/* Routing Strategy Selector */}
                            <Controller
                                name="pickProcessorStrategy"
                                control={control}
                                render={({ field }) => (
                                    <div className="space-y-2">
                                        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                            {t('paymentFlows:form.fields.routing_strategy.label')}
                                        </Label>
                                        <div className="grid grid-cols-2 gap-2">
                                            {/* Custom Weights Option */}
                                            <button
                                                type="button"
                                                onClick={() => field.onChange('weighted')}
                                                className={cn(
                                                    "relative flex items-center gap-2 rounded-lg border-2 p-2.5 text-left transition-all hover:bg-muted/50",
                                                    field.value === 'weighted'
                                                        ? "border-primary bg-primary/5"
                                                        : "border-muted-foreground/20"
                                                )}
                                            >
                                                <div className={cn(
                                                    "flex size-7 items-center justify-center rounded-md shrink-0",
                                                    field.value === 'weighted'
                                                        ? "bg-primary text-primary-foreground"
                                                        : "bg-muted text-muted-foreground"
                                                )}>
                                                    <Sliders className="size-3.5" />
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="text-sm font-medium truncate">
                                                        {t('paymentFlows:form.fields.routing_strategy.weighted')}
                                                    </p>
                                                    <p className="text-[10px] text-muted-foreground truncate">
                                                        {t('paymentFlows:form.fields.routing_strategy.weighted_hint')}
                                                    </p>
                                                </div>
                                            </button>

                                            {/* Automatic Option */}
                                            <button
                                                type="button"
                                                onClick={() => field.onChange('automatic')}
                                                className={cn(
                                                    "relative flex items-center gap-2 rounded-lg border-2 p-2.5 text-left transition-all hover:bg-muted/50",
                                                    field.value === 'automatic'
                                                        ? "border-primary bg-primary/5"
                                                        : "border-muted-foreground/20"
                                                )}
                                            >
                                                <div className={cn(
                                                    "flex size-7 items-center justify-center rounded-md shrink-0",
                                                    field.value === 'automatic'
                                                        ? "bg-primary text-primary-foreground"
                                                        : "bg-muted text-muted-foreground"
                                                )}>
                                                    <Sparkles className="size-3.5" />
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="text-sm font-medium truncate">
                                                        {t('paymentFlows:form.fields.routing_strategy.automatic')}
                                                    </p>
                                                    <p className="text-[10px] text-muted-foreground truncate">
                                                        {t('paymentFlows:form.fields.routing_strategy.automatic_hint')}
                                                    </p>
                                                </div>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            />

                            {/* Weight Distribution Bar - only show for weighted strategy */}
                            {watchedStrategy === 'weighted' && watchedProcessorConfigs && (() => {
                                const activeConfigs = watchedProcessorConfigs.filter((c) => !c.disabled)
                                const totalWeight = calculateTotalWeight(watchedProcessorConfigs)
                                
                                if (activeConfigs.length === 0) return null
                                
                                return (
                                    <div className="space-y-3 rounded-lg border bg-muted/30 p-4">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-medium">{t('paymentFlows:form.sections.weight_distribution')}</span>
                                            <div className="flex items-center gap-2">
                                                <span className={cn(
                                                    "text-sm font-medium",
                                                    totalWeight === 100 ? "text-emerald-600" : "text-amber-600"
                                                )}>
                                                    {totalWeight}%
                                                </span>
                                                {/* Normalize: Scale existing weights proportionally to 100% - only when not at 100% */}
                                                {totalWeight !== 100 && totalWeight > 0 && (
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        className="h-7 text-xs"
                                                        onClick={() => {
                                                            // Normalize: scale weights proportionally to sum to 100%
                                                            if (totalWeight === 0) return
                                                            
                                                            const scaleFactor = 100 / totalWeight
                                                            let accumulated = 0
                                                            const activeCount = activeConfigs.length
                                                            
                                                            watchedProcessorConfigs.forEach((config, idx) => {
                                                                if (config.disabled) return
                                                                
                                                                // For the last active processor, use remainder to ensure exactly 100%
                                                                const isLast = activeConfigs.indexOf(config) === activeCount - 1
                                                                const scaledWeight = isLast 
                                                                    ? 100 - accumulated 
                                                                    : Math.round(config.weight * scaleFactor)
                                                                
                                                                accumulated += scaledWeight
                                                                setValue(`processorConfigs.${idx}.weight`, scaledWeight)
                                                            })
                                                        }}
                                                    >
                                                        <Maximize2 className="mr-1.5 size-3" />
                                                        {t('paymentFlows:form.fields.weight.normalize')}
                                                    </Button>
                                                )}
                                                {/* Balance: Distribute evenly - always available */}
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-7 text-xs"
                                                    onClick={() => {
                                                        // Auto-balance weights evenly
                                                        const activeCount = activeConfigs.length
                                                        if (activeCount === 0) return
                                                        
                                                        const baseWeight = Math.floor(100 / activeCount)
                                                        const remainder = 100 % activeCount
                                                        
                                                        // Update each processor weight individually using setValue
                                                        let activeIdx = 0
                                                        watchedProcessorConfigs.forEach((config, idx) => {
                                                            if (config.disabled) return
                                                            const newWeight = baseWeight + (activeIdx < remainder ? 1 : 0)
                                                            setValue(`processorConfigs.${idx}.weight`, newWeight)
                                                            activeIdx++
                                                        })
                                                    }}
                                                >
                                                    <Scale className="mr-1.5 size-3" />
                                                    {t('paymentFlows:form.fields.weight.balance')}
                                                </Button>
                                            </div>
                                        </div>
                                        
                                        {/* Visual weight bar */}
                                        <div className="flex h-3 overflow-hidden rounded-full bg-muted">
                                            {activeConfigs.map((config, idx) => (
                                                <div
                                                    key={config.processorId || idx}
                                                    className={cn(
                                                        "h-full transition-all duration-200",
                                                        WEIGHT_COLORS[idx % WEIGHT_COLORS.length]
                                                    )}
                                                    style={{ width: `${config.weight}%` }}
                                                />
                                            ))}
                                            {totalWeight < 100 && (
                                                <div
                                                    className="h-full bg-muted-foreground/20"
                                                    style={{ width: `${100 - totalWeight}%` }}
                                                />
                                            )}
                                        </div>
                                        
                                        {/* Legend */}
                                        <div className="flex flex-wrap gap-3 text-xs">
                                            {activeConfigs.map((config, idx) => (
                                                <div key={config.processorId || idx} className="flex items-center gap-1.5">
                                                    <div className={cn("size-2.5 rounded-full", WEIGHT_COLORS[idx % WEIGHT_COLORS.length])} />
                                                    <span className="text-muted-foreground">{config.weight}%</span>
                                                </div>
                                            ))}
                                            {totalWeight < 100 && (
                                                <div className="flex items-center gap-1.5">
                                                    <div className="size-2.5 rounded-full bg-muted-foreground/20" />
                                                    <span className="text-muted-foreground">{t('paymentFlows:form.fields.weight.unallocated', { percent: 100 - totalWeight })}</span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-[11px] text-blue-800 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-200">
                                            <Info className="mt-0.5 size-3 shrink-0" />
                                            <p>
                                                {t('paymentFlows:form.fields.weight.daily_window_info')}
                                            </p>
                                        </div>
                                        {isRoutingPinsEnabled && <RoutingPrecedenceHelp />}
                                    </div>
                                )
                            })()}
                            
                            {processorConfigFields.length === 0 ? (
                                <p className="text-muted-foreground text-sm">
                                    {t('paymentFlows:form.empty_states.no_processors')}
                                </p>
                            ) : (
                                processorConfigFields.map((field, index) => (
                                    <div
                                        key={field.id}
                                        className="flex items-start gap-3 rounded-lg border p-3"
                                    >
                                        <div className="flex-1 space-y-3">
                                            <Controller
                                                name={`processorConfigs.${index}.processorId`}
                                                control={control}
                                                render={({ field: processorField }) => (
                                                    <Controller
                                                        name="processorConfigs"
                                                        control={control}
                                                        render={({ field: allConfigsField }) => (
                                                            <ProcessorSelector
                                                                processorId={processorField.value}
                                                                onProcessorChange={processorField.onChange}
                                                                excludeProcessorIds={allConfigsField.value
                                                                    .map((c) => c.processorId)
                                                                    .filter((_, i) => i !== index)}
                                                                label={t('paymentFlows:form.fields.processor.label')}
                                                                required
                                                            />
                                                        )}
                                                    />
                                                )}
                                            />

                                            {/* Processor-level switches: they apply whatever the weights and conditions say */}
                                            <div className="flex flex-wrap gap-3">
                                                <Controller
                                                    name={`processorConfigs.${index}.disabled`}
                                                    control={control}
                                                    render={({ field: disabledField }) => (
                                                        <div className="flex items-center space-x-1.5">
                                                            <Switch
                                                                id={`processor-disabled-${index}`}
                                                                checked={disabledField.value}
                                                                onCheckedChange={disabledField.onChange}
                                                                className="scale-90"
                                                            />
                                                            <Label htmlFor={`processor-disabled-${index}`} className="text-xs">
                                                                {t('paymentFlows:form.fields.disabled.label')}
                                                            </Label>
                                                        </div>
                                                    )}
                                                />
                                                <Controller
                                                    name={`processorConfigs.${index}.nonStickable`}
                                                    control={control}
                                                    render={({ field: nonStickableField }) => (
                                                        <div className="flex items-center space-x-1.5">
                                                            <Switch
                                                                id={`processor-nonstickable-${index}`}
                                                                checked={nonStickableField.value}
                                                                onCheckedChange={nonStickableField.onChange}
                                                                className="scale-90"
                                                            />
                                                            <Label htmlFor={`processor-nonstickable-${index}`} className="text-xs">
                                                                {t('paymentFlows:form.fields.non_stickable.label')}
                                                            </Label>
                                                        </div>
                                                    )}
                                                />
                                            </div>

                                            <Controller
                                                name="pickProcessorStrategy"
                                                control={control}
                                                render={({ field: strategyField }) => (
                                                    <>
                                                        {strategyField.value === 'weighted' && (
                                                            <Controller
                                                                name={`processorConfigs.${index}.weight`}
                                                                control={control}
                                                                render={({ field: weightField }) => (
                                                                    <Controller
                                                                        name={`processorConfigs.${index}.disabled`}
                                                                        control={control}
                                                                        render={({ field: disabledField }) => (
                                                                            <div className={cn("space-y-2", disabledField.value && "opacity-50")}>
                                                                                <Separator />
                                                                                <div className="flex items-center justify-between">
                                                                                    <Label htmlFor={`processor-weight-${index}`} className={GROUP_LABEL_CLASSES}>
                                                                                        {t('paymentFlows:form.fields.weight.label')}
                                                                                    </Label>
                                                                                    <div className="flex items-center gap-1.5">
                                                                                        <Input
                                                                                            id={`processor-weight-${index}`}
                                                                                            type="number"
                                                                                            min="0"
                                                                                            max="100"
                                                                                            className="h-7 w-16 text-center text-sm"
                                                                                            value={weightField.value}
                                                                                            onChange={(e) => weightField.onChange(Number(e.target.value))}
                                                                                            onBlur={weightField.onBlur}
                                                                                            disabled={disabledField.value}
                                                                                        />
                                                                                        <Percent className="size-3.5 text-muted-foreground" />
                                                                                    </div>
                                                                                </div>
                                                                                <Slider
                                                                                    value={[weightField.value]}
                                                                                    onValueChange={(values) => weightField.onChange(values[0])}
                                                                                    min={0}
                                                                                    max={100}
                                                                                    step={1}
                                                                                    disabled={disabledField.value}
                                                                                    className={cn(
                                                                                        "[&_[role=slider]]:h-3.5 [&_[role=slider]]:w-3.5",
                                                                                        WEIGHT_COLORS[index % WEIGHT_COLORS.length].replace('bg-', '[&_[data-orientation=horizontal]>.bg-primary]:bg-')
                                                                                    )}
                                                                                />
                                                                                {/* Quick weight buttons */}
                                                                                <div className="flex gap-1">
                                                                                    {[10, 25, 50, 75, 100].map((val) => (
                                                                                        <Button
                                                                                            key={val}
                                                                                            type="button"
                                                                                            variant={weightField.value === val ? "default" : "outline"}
                                                                                            size="sm"
                                                                                            className="h-5 flex-1 text-[10px]"
                                                                                            onClick={() => weightField.onChange(val)}
                                                                                            disabled={disabledField.value}
                                                                                        >
                                                                                            {val}%
                                                                                        </Button>
                                                                                    ))}
                                                                                </div>
                                                                                <p className={GROUP_CAPTION_CLASSES}>
                                                                                    {t('paymentFlows:form.fields.weight.role_caption')}
                                                                                </p>
                                                                            </div>
                                                                        )}
                                                                    />
                                                                )}
                                                            />
                                                        )}
                                                    </>
                                                )}
                                            />

                                            {/* Disabled Card Brands */}
                                            <Controller
                                                name={`processorConfigs.${index}.disabledCardBrands`}
                                                control={control}
                                                render={({ field: cardBrandsField }) => {
                                                    const disabledBrands = cardBrandsField.value || []
                                                    const toggleBrand = (brand: string) => {
                                                        if (disabledBrands.includes(brand)) {
                                                            cardBrandsField.onChange(disabledBrands.filter((b: string) => b !== brand))
                                                        } else {
                                                            cardBrandsField.onChange([...disabledBrands, brand])
                                                        }
                                                    }
                                                    return (
                                                        <div className="space-y-1.5">
                                                            <Separator />
                                                            <div className="space-y-0.5">
                                                                <Label className={GROUP_LABEL_CLASSES}>
                                                                    {t('paymentFlows:form.fields.disabled_card_brands.label')}
                                                                </Label>
                                                                <p className={GROUP_CAPTION_CLASSES}>
                                                                    {t('paymentFlows:form.fields.disabled_card_brands.role_caption')}
                                                                </p>
                                                            </div>
                                                            <div className="flex flex-wrap gap-1.5">
                                                                {CARD_BRANDS.map((brand) => {
                                                                    const isBlocked = disabledBrands.includes(brand)
                                                                    return (
                                                                        <button
                                                                            key={brand}
                                                                            type="button"
                                                                            onClick={() => toggleBrand(brand)}
                                                                            className={cn(
                                                                                'rounded-md border px-2 py-0.5 text-[11px] font-medium transition-colors',
                                                                                isBlocked
                                                                                    ? 'border-red-300 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/50 dark:text-red-300'
                                                                                    : 'border-muted-foreground/20 text-muted-foreground hover:border-muted-foreground/40',
                                                                            )}
                                                                        >
                                                                            {isBlocked ? '✕ ' : ''}{CARD_BRAND_LABELS[brand] || brand}
                                                                        </button>
                                                                    )
                                                                })}
                                                            </div>
                                                            {disabledBrands.length > 0 && (
                                                                <p className="text-[10px] text-red-600 dark:text-red-400">
                                                                    {t('paymentFlows:form.fields.disabled_card_brands.blocked_count', { count: disabledBrands.length })}
                                                                </p>
                                                            )}
                                                        </div>
                                                    )
                                                }}
                                            />

                                            {isRoutingPinsEnabled && watchedStrategy === 'weighted' && (
                                                <ProcessorRoutingPins
                                                    processorIndex={index}
                                                    isProcessorDisabled={Boolean(
                                                        watchedProcessorConfigs?.[index]?.disabled,
                                                    )}
                                                />
                                            )}
                                        </div>

                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => removeProcessorConfig(index)}
                                        >
                                            <X className="size-4" />
                                        </Button>
                                    </div>
                                ))
                            )}

                            {/* Add Processor Button */}
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="w-full"
                                onClick={handleAddProcessor}
                            >
                                {t('paymentFlows:form.buttons.add_processor')}
                            </Button>
                        </CardContent>
                    </Card>

                    {/* Fallback Processors */}
                    <Card>
                        <CardHeader>
                            <CardTitle>
                                {t('paymentFlows:form.sections.fallback_processors')}
                            </CardTitle>
                            <CardDescription>
                                {t('paymentFlows:form.sections.fallback_processors_description')}
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {/* Fallback Mode Toggle */}
                            <div className="flex items-center justify-between rounded-lg border p-3 bg-muted/30">
                                <div className="space-y-0.5">
                                    <Label htmlFor="fallbackMode" className="text-sm font-medium">
                                        {t('paymentFlows:form.fields.fallback_mode.label')}
                                    </Label>
                                    <p className="text-muted-foreground text-xs">
                                        {t('paymentFlows:form.fields.fallback_mode.help')}
                                    </p>
                                </div>
                                <Controller
                                    name="fallbackMode"
                                    control={control}
                                    render={({ field }) => (
                                        <Switch
                                            id="fallbackMode"
                                            checked={field.value}
                                            onCheckedChange={field.onChange}
                                        />
                                    )}
                                />
                            </div>

                            {/* Max Retries - only show when fallback enabled */}
                            <Controller
                                name="fallbackMode"
                                control={control}
                                render={({ field: fallbackField }) => (
                                    <>
                                        {fallbackField.value && (
                                            <>
                                                <Controller
                                                    name="maxFallbackRetries"
                                                    control={control}
                                                    render={({ field: retriesField }) => (
                                                        <div className="space-y-2">
                                                            <Label htmlFor="maxFallbackRetries" className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                                                {t('paymentFlows:form.fields.max_retries.label')}
                                                            </Label>
                                                            <Input
                                                                id="maxFallbackRetries"
                                                                type="number"
                                                                min="0"
                                                                max="10"
                                                                className="w-24"
                                                                value={retriesField.value}
                                                                onChange={(e) => retriesField.onChange(Number(e.target.value))}
                                                                onBlur={retriesField.onBlur}
                                                            />
                                                            {errors.maxFallbackRetries && (
                                                                <p className="text-xs text-red-600">
                                                                    {errors.maxFallbackRetries.message}
                                                                </p>
                                                            )}
                                                        </div>
                                                    )}
                                                />

                                                {/* Fallback Processor List */}
                                                <div className="space-y-2 pt-2">
                                                    <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                                        {t('paymentFlows:form.sections.fallback_queue')}
                                                    </Label>
                                                    {fallbackConfigFields.length === 0 ? (
                                                        <p className="text-muted-foreground text-sm py-2">
                                                            {t('paymentFlows:form.empty_states.no_fallback')}
                                                        </p>
                                                    ) : (
                                                        <div className="space-y-2">
                                                            {fallbackConfigFields.map((field, index) => (
                                                                <div
                                                                    key={field.id}
                                                                    className="flex items-center gap-3 rounded-lg border p-3"
                                                                >
                                                                    <div className="flex-1">
                                                                        <Controller
                                                                            name={`fallbackProcessorConfigs.${index}.processorId`}
                                                                            control={control}
                                                                            render={({ field: processorField }) => (
                                                                                <ProcessorSelector
                                                                                    processorId={processorField.value}
                                                                                    onProcessorChange={(processorId) => {
                                                                                        processorField.onChange(processorId)
                                                                                    }}
                                                                                    label={t('paymentFlows:form.fields.fallback_processor.label', {
                                                                                        index: index + 1,
                                                                                    })}
                                                                                    required
                                                                                />
                                                                            )}
                                                                        />
                                                                    </div>

                                                                    <Button
                                                                        type="button"
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        className="h-8 w-8 shrink-0"
                                                                        onClick={() => removeFallbackConfig(index)}
                                                                    >
                                                                        <X className="size-4" />
                                                                    </Button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {/* Add Fallback Button */}
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        className="w-full"
                                                        onClick={handleAddFallbackProcessor}
                                                    >
                                                        {t('paymentFlows:form.buttons.add_fallback')}
                                                    </Button>
                                                </div>
                                            </>
                                        )}
                                    </>
                                )}
                            />
                        </CardContent>
                    </Card>
                    </div>
                </div>
            </form>
        </FormProvider>
    )
}

